/**
 * Shared billing presentation helpers (FR-BILLING-01/06).
 *
 * Pure functions and label tables only — no React — so the plan cards, the
 * usage bars, the invoice table and the trial prompts all format money, caps
 * and dates identically. Keys mirror the service catalog exactly
 * (`services/billing/src/constants.ts`); an unknown key falls back to the raw
 * key rather than disappearing from the UI.
 */

/** Human labels for the catalog's feature keys (`feature_bundle.features`). */
export const FEATURE_LABELS: Record<string, string> = {
  play_entry: 'Play Entry',
  game_summary: 'Game Summary',
  play_archive: 'Play Archive',
  stat_tracker: 'Stat Tracker',
  coach_highlight: 'Coaches Highlight',
  playbook: 'Playbook Database',
  playbook_unlimited: 'Unlimited playbook entries',
  hudl_integration: 'Hudl CSV import / export',
  live_video_embed: 'Live video embed',
  multi_team: 'Multi-team support',
  api_access: 'API access',
  custom_roles: 'Custom RBAC roles',
  saml_scim: 'SAML SSO / SCIM provisioning',
};

export const LIMIT_LABELS: Record<string, string> = {
  seats: 'Seats',
  teams: 'Teams',
  games_per_season: 'Games this season',
  storage_bytes: 'Media storage',
  api_rate_per_min: 'API requests / minute',
  ws_connections: 'Live connections',
  history_seasons: 'Seasons of history',
};

/**
 * Customer-facing label for a limit key. Known keys use LIMIT_LABELS; any key
 * the billing service adds later is humanised (`foo_bar` → `Foo bar`) so a raw
 * snake_case API key never reaches the screen.
 */
export function limitLabel(limitKey: string): string {
  const known = LIMIT_LABELS[limitKey];
  if (known !== undefined) return known;
  const words = limitKey.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (words === '') return limitKey;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Display order for the caps list on every plan card. */
export const LIMIT_ORDER = ['seats', 'games_per_season', 'storage_bytes', 'teams'];

/** Cents → `$149` / `$1,499` / `$12.50`. `null` renders as an em dash. */
export function formatMoneyCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  if (cents === 0) return '$0';
  const dollars = cents / 100;
  return dollars % 1 === 0
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

/** `null` limits mean unlimited — never render a bare "null". */
export function formatLimit(limitKey: string, value: number | null): string {
  if (value === null) return 'Unlimited';
  if (limitKey === 'storage_bytes') {
    const gb = value / 1_000_000_000;
    return `${gb >= 10 ? Math.round(gb) : gb.toFixed(1)} GB`;
  }
  return value.toLocaleString('en-US');
}

export function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Features present on `from` and absent on `to`, as display labels. */
export function planFeatureLosses(
  from: Record<string, boolean>,
  to: Record<string, boolean>
): string[] {
  return Object.entries(from)
    .filter(([key, isIncluded]) => isIncluded && to[key] !== true)
    .map(([key]) => FEATURE_LABELS[key] ?? key);
}

/** Caps that shrink when moving `from` → `to`, as "Seats: 50 → 15" strings. */
export function limitLosses(
  from: Record<string, number | null>,
  to: Record<string, number | null>
): string[] {
  return LIMIT_ORDER.flatMap((key) => {
    const before = from[key] ?? null;
    const after = to[key] ?? null;
    const isReduction = before === null ? after !== null : after !== null && after < before;
    if (!isReduction) return [];
    return [`${limitLabel(key)}: ${formatLimit(key, before)} → ${formatLimit(key, after)}`];
  });
}

/** `/month` or `/year`, matching the cadence shown next to a price. */
export function cadenceSuffix(cadence: string): string {
  return cadence === 'annual' ? '/year' : '/month';
}
